import { ReactNode, useState } from 'react';
import Button from '../Button';
import OffCanvas from '../common/OffCanvas';

interface IJobListOffCanvas {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  position?: 'left' | 'right';
  title?: string;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
}

const JobListOffCanvas = ({ 
  isOpen, 
  onClose, 
  children, 
  position = 'right', 
  title = 'Select Job',
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange
}: IJobListOffCanvas) => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  const handlePageClick = (page: number) => {
    setCurrentPage(page);
    if (onPageChange) {
      onPageChange(page);
    }
  };
  
  // Generate page numbers
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    // Logic to determine which page numbers to show
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${i === currentPage ? 'active' : ''}`}>
          <a 
            className="page-link" 
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageClick(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }
    
    return pages;
  };
  
  // Custom footer with pagination and cancel button
  const customFooter = (
    <div className="d-flex justify-content-between align-items-center w-100">
      {totalPages > 0 ? (
        <ul className="pagination mb-0">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <a 
              className="page-link" 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (currentPage > 1) handlePageClick(currentPage - 1);
              }}
            >
              <i className="fal fa-chevron-left"></i>
            </a>
          </li>
          
          {renderPageNumbers()}
          
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <a 
              className="page-link" 
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (currentPage < totalPages) handlePageClick(currentPage + 1);
              }}
            >
              <i className="fal fa-chevron-right"></i>
            </a>
          </li>
        </ul>
      ) : <div></div>} {/* Empty div to maintain flex layout when no pagination */}
      
      <Button
        text="Cancel"
        variant="outline-secondary"
        onClick={onClose}
      />
    </div>
  );
  
  return (
    <OffCanvas
      isOpen={isOpen}
      onClose={onClose}
      position={position}
      title={title}
      width="50%"
      mobileWidth="100%"
      zIndex={1050}
      footerContent={customFooter}
    >
      {children}
    </OffCanvas>
  );
};

export default JobListOffCanvas; 